import React, { useState } from 'react';
import { Table, Tag, Space, Tooltip, Dropdown, Button, Toast } from '@douyinfe/semi-ui';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import { IconDownload, IconChevronDown } from '@douyinfe/semi-icons';
import { stringToColor, renderModelPrice } from '../helpers/render';
import { copy, timestamp2string, isMobile } from '../helpers/utils';
import { getCacheTokens, quotaToUSD, formatFull, parseOther, isUsageLog } from '../helpers/usage';
import { ITEMS_PER_PAGE } from '../constants';
import CollapsiblePanel from './CollapsiblePanel';

function renderIsStream(bool) {
  return bool ? (
    <Tag color="blue" size="small">流</Tag>
  ) : (
    <Tag color="purple" size="small">非流</Tag>
  );
}

function renderUseTime(time) {
  const t = parseInt(time);
  const color = t < 101 ? 'green' : t < 300 ? 'orange' : 'red';
  return <Tag color={color} size="small">{t}s</Tag>;
}

const CACHE_CHIPS = [
  { key: 'read', label: '读', color: '#10b981', tip: '缓存读取（命中）' },
  { key: 'write5m', label: '写5m', color: '#3b82f6', tip: '缓存写入（5分钟）' },
  { key: 'write1h', label: '写1h', color: '#8b5cf6', tip: '缓存写入（1小时）' },
  { key: 'writeOther', label: '写', color: '#06b6d4', tip: '缓存写入' },
];

// 调用明细表 + CSV 导出入口
const DetailTable = ({ logs, loading, onExport }) => {
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);

  const copyText = async (text) => {
    if (await copy(text)) Toast.success('已复制：' + text);
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      render: (ts) => timestamp2string(ts),
      sorter: (a, b) => a.created_at - b.created_at,
      width: 165,
    },
    {
      title: '模型',
      dataIndex: 'model_name',
      render: (text, record) =>
        isUsageLog(record) ? (
          <Tag
            color={stringToColor(text || '')}
            size="large"
            style={{ cursor: 'pointer' }}
            onClick={() => copyText(text)}
          >
            {text}
          </Tag>
        ) : null,
      sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
    },
    {
      title: '用时',
      dataIndex: 'use_time',
      render: (text, record) =>
        (record.model_name || '').startsWith('mj_') ? null : (
          <Space>
            {renderUseTime(text)}
            {renderIsStream(record.is_stream)}
          </Space>
        ),
      sorter: (a, b) => a.use_time - b.use_time,
    },
    {
      title: '提示',
      dataIndex: 'prompt_tokens',
      render: (text, record) => {
        if ((record.model_name || '').startsWith('mj_')) return null;
        if (!isUsageLog(record)) return null;
        const cache = getCacheTokens(record);
        return (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>{formatFull(text)}</span>
            {CACHE_CHIPS.map(({ key, label, color, tip }) =>
              cache[key] > 0 ? (
                <Tooltip content={tip} key={key}>
                  <span className="cache-chip">
                    <span className="dot" style={{ background: color }} />
                    {label} {formatFull(cache[key])}
                  </span>
                </Tooltip>
              ) : null
            )}
          </div>
        );
      },
      sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
    },
    {
      title: '补全',
      dataIndex: 'completion_tokens',
      render: (text, record) =>
        parseInt(text) > 0 && isUsageLog(record) ? formatFull(text) : null,
      sorter: (a, b) => a.completion_tokens - b.completion_tokens,
    },
    {
      title: '花费',
      dataIndex: 'quota',
      render: (quota, record) =>
        isUsageLog(record) ? (
          <Tooltip content={`原始额度 ${formatFull(quota)}`}>
            <span>{quotaToUSD(quota).toFixed(2)}</span>
          </Tooltip>
        ) : null,
      sorter: (a, b) => a.quota - b.quota,
    },
    {
      title: '详情',
      dataIndex: 'content',
      width: 260,
      render: (text, record) => {
        const other = parseOther(record);
        if (Object.keys(other).length === 0) {
          return (
            <Paragraph ellipsis={{ rows: 2, showTooltip: { type: 'popover' } }} style={{ maxWidth: 236 }}>
              {text}
            </Paragraph>
          );
        }
        const content = renderModelPrice(
          record.prompt_tokens,
          record.completion_tokens,
          other.model_ratio,
          other.model_price,
          other.completion_ratio,
          other.group_ratio
        );
        return (
          <Tooltip content={content}>
            <Paragraph ellipsis={{ rows: 2 }} style={{ maxWidth: 236 }}>
              {text}
            </Paragraph>
          </Tooltip>
        );
      },
    },
  ];

  const exportMenu = (
    <Dropdown
      trigger="click"
      position="bottomRight"
      render={
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => onExport('detail')}>
            导出调用明细 CSV
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onExport('model')}>
            导出按模型统计 CSV
          </Dropdown.Item>
          <Dropdown.Item onClick={() => onExport('daily')}>
            导出按日统计 CSV
          </Dropdown.Item>
        </Dropdown.Menu>
      }
    >
      <Button
        icon={<IconDownload />}
        theme="light"
        type="primary"
        disabled={logs.length === 0}
      >
        导出 CSV <IconChevronDown size="small" />
      </Button>
    </Dropdown>
  );

  return (
    <CollapsiblePanel
      className="fade-up fade-up-4"
      storageKey="collapse:detail-table"
      title="调用明细"
      sub={logs.length > 0 ? `${formatFull(logs.length)} 条记录（已应用筛选）` : ''}
      extra={exportMenu}
    >
      <Table
        columns={columns}
        dataSource={logs}
        loading={loading}
        rowKey={(record) => `${record.created_at}-${record.id ?? record.model_name}-${record.quota}`}
        size="middle"
        empty="暂无数据"
        pagination={{
          pageSize,
          hideOnSinglePage: true,
          showSizeChanger: !isMobile(),
          pageSizeOpts: [10, 20, 50, 100],
          onPageSizeChange: setPageSize,
          showTotal: isMobile() ? undefined : (total) => `共 ${total} 条`,
          showQuickJumper: !isMobile(),
          total: logs.length,
        }}
        scroll={{ x: 'max-content' }}
      />
    </CollapsiblePanel>
  );
};

export default DetailTable;
